// Board changes that make the rules in force readable at a glance (docs/09 §7.1, pillar 5): each
// Resolution that changes a room leaves a small mark in it, toll Decrees put barriers at the doors
// they tax, and Interns brought forward by Labour Accord wait at the main entrance. Pure
// presentation, driven by the view (sync) and by the Director's beats (reveal / remove).
import * as THREE from 'three';
import { gsap } from 'gsap';
import type { DefId, PlayerView } from '@bcm/rules';
import type { PropLibrary } from './assets';
import { ROOM_FLOOR_Y } from './ministry';
import { toonFrom } from './materials';
import { AMBIENT } from './pacing';
import { dust, glint, motes } from './fx3d';
import { C, SEAL_COLORS, SEAL_TRIMS } from '../game/content';
import { drawIcon } from '../game/icons';

/** Rooms whose toll a Decree charges, and where its barrier stands (just outside the door). */
const TOLL_SPOTS: Record<string, { x: number; z: number }> = {
  'mana-conduit': { x: 13.45, z: 6.2 },
  arcanium: { x: -11.35, z: 6.0 },
};

/** The Resolutions that leave a mark on the board, and where (for the camera and the motes). */
const RESOLUTION_SPOTS: Record<string, { x: number; y: number; z: number }> = {
  'resolution.storm_power': { x: 9.6, y: 0.8, z: -10.4 },
  'resolution.open_shelves': { x: -14.3, y: 1, z: 8.6 },
  'resolution.ceremonial_stipend': { x: 22.7, y: 1, z: 16.6 },
  'resolution.night_market': { x: 10.3, y: 1.2, z: 5.1 },
  'resolution.memos_fly_free': { x: 1.25, y: 2.6, z: 11.3 },
};

const brass = () => toonFrom({ color: '#c9a14e', metalness: 0.9 });
const iron = () => toonFrom({ color: '#2e2a28', metalness: 0.8 });

function box(w: number, h: number, d: number, m: THREE.Material, x = 0, y = 0, z = 0): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function canvasTex(w: number, h: number, draw: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  draw(c.getContext('2d')!);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

export class BoardDecor {
  private items = new Map<string, THREE.Object3D>();
  /** Keys mid-animation: sync leaves them alone. */
  private busy = new Set<string>();
  reducedMotion = false;

  constructor(private scene: THREE.Scene) {}

  /** What the view says should be on the board. */
  private wanted(view: PlayerView): Set<string> {
    const out = new Set<string>();
    for (const r of view.resolutions?.inForce ?? []) if (RESOLUTION_SPOTS[r.resolution]) out.add(`res:${r.resolution}`);
    for (const d of view.decrees ?? []) {
      for (const m of C.decrees.get(d.decree)?.effects ?? []) if (m.type === 'toll' && TOLL_SPOTS[m.location]) out.add(`toll:${m.location}`);
    }
    // Labour Accord: Interns brought forward wait at the entrance until they are taken on.
    const planned = view.options.internRound;
    if (view.internRound != null && planned != null && view.internRound < planned && view.round < view.internRound && view.phase === 'action') out.add('interns');
    return out;
  }

  /** Place or clear everything at once to match the view (no animation). */
  sync(view: PlayerView, lib: PropLibrary | null): void {
    const want = this.wanted(view);
    for (const [key, o] of this.items) {
      if (!want.has(key) && !this.busy.has(key)) this.drop(key, o);
    }
    for (const key of want) if (!this.items.has(key) && !this.busy.has(key)) this.make(key, view, lib);
  }

  private drop(key: string, o: THREE.Object3D): void {
    gsap.killTweensOf(o.rotation);
    o.traverse((c) => gsap.killTweensOf((c as THREE.Object3D).position));
    this.scene.remove(o);
    o.traverse((c) => (c as THREE.Mesh).geometry?.dispose?.());
    this.items.delete(key);
  }

  private make(key: string, view: PlayerView, lib: PropLibrary | null): THREE.Object3D | null {
    let o: THREE.Object3D | null = null;
    if (key.startsWith('res:')) o = this.resolutionPiece(key.slice(4));
    else if (key.startsWith('toll:')) o = tollGate(key.slice(5));
    else if (key === 'interns') o = interns(view, lib);
    if (!o) return null;
    this.items.set(key, o);
    this.scene.add(o);
    return o;
  }

  /** Where a Resolution's mark stands (for effects and the camera), if it has one. */
  spot(resolution: DefId): THREE.Vector3 | null {
    const s = RESOLUTION_SPOTS[resolution];
    return s ? new THREE.Vector3(s.x, s.y, s.z) : null;
  }

  /**
   * A Resolution comes into force: its mark (if it has one) arrives with a pop and a few motes, and
   * Labour Accord's Interns walk up to the door. Resolves when it has settled.
   */
  async reveal(view: PlayerView, resolution: DefId, lib: PropLibrary | null, speed = 1): Promise<void> {
    const keys = [`res:${resolution}`, ...(resolution === 'resolution.labour_accord' ? ['interns'] : [])];
    const want = this.wanted(view);
    const jobs: Promise<void>[] = [];
    for (const key of keys) {
      if (!want.has(key) || this.items.has(key)) continue;
      const o = this.make(key, view, lib);
      if (!o) continue;
      this.busy.add(key);
      jobs.push(popIn(o, speed, this.reducedMotion).then(() => void this.busy.delete(key)));
      const at = new THREE.Box3().setFromObject(o).getCenter(new THREE.Vector3());
      motes(this.scene, at, { count: 10, spread: 1.2, rise: 1.8, speed, reduced: this.reducedMotion });
    }
    await Promise.all(jobs);
  }

  /** A toll Decree comes into force: its barrier drops across the door. */
  async addToll(view: PlayerView, decree: DefId, speed = 1): Promise<void> {
    for (const m of C.decrees.get(decree)?.effects ?? []) {
      if (m.type !== 'toll' || !TOLL_SPOTS[m.location]) continue;
      const key = `toll:${m.location}`;
      if (this.items.has(key)) continue;
      const o = this.make(key, view, null);
      if (!o) continue;
      this.busy.add(key);
      await popIn(o, speed, this.reducedMotion);
      this.busy.delete(key);
    }
  }

  /** A toll Decree is struck (Free Roads, a Repeal): its barrier lifts and the booth is carted off. */
  async removeToll(decree: DefId, speed = 1): Promise<void> {
    for (const m of C.decrees.get(decree)?.effects ?? []) {
      if (m.type !== 'toll') continue;
      const key = `toll:${m.location}`;
      const o = this.items.get(key);
      if (!o) continue;
      this.busy.add(key);
      const arm = o.getObjectByName('arm');
      const tl = gsap.timeline();
      tl.timeScale(this.reducedMotion ? speed * 1.6 : speed);
      // The arm runs along z from its post: it swings up about x.
      if (arm) tl.to(arm.rotation, { x: -1.3, duration: 0.35, ease: 'back.out(2)' }, 0);
      tl.call(() => dust(this.scene, o.position.clone().setY(0.1), { count: 5, size: 1, speed, reduced: this.reducedMotion }), [], 0.35);
      tl.to(o.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.3, ease: 'back.in(2)' }, 0.55);
      await new Promise<void>((r) => tl.eventCallback('onComplete', () => r()));
      this.busy.delete(key);
      this.drop(key, o);
    }
  }

  /** The Interns go in to start their shift (the Afternoon Shift arrives early). */
  async dismissInterns(speed = 1): Promise<void> {
    const o = this.items.get('interns');
    if (!o) return;
    this.busy.add('interns');
    const tl = gsap.timeline();
    tl.timeScale(speed);
    o.children.forEach((c, i) => {
      tl.to(c.position, { z: c.position.z - 2.2, y: 0.9, duration: 0.3, ease: 'power1.out' }, i * 0.06);
      tl.to(c.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.2 }, i * 0.06 + 0.2);
    });
    await new Promise<void>((r) => tl.eventCallback('onComplete', () => r()));
    this.busy.delete('interns');
    this.drop('interns', o);
  }

  private resolutionPiece(id: DefId): THREE.Object3D | null {
    switch (id) {
      case 'resolution.storm_power':
        return stormPipe();
      case 'resolution.open_shelves':
        return openShelvesSign();
      case 'resolution.ceremonial_stipend':
        return tipJar();
      case 'resolution.night_market':
        return marketStall();
      case 'resolution.memos_fly_free':
        return memoPlanes(this.reducedMotion);
      default:
        return null;
    }
  }

  dispose(): void {
    for (const [key, o] of [...this.items]) this.drop(key, o);
  }
}

/** Scale in with a bounce, then a glint. */
function popIn(o: THREE.Object3D, speed: number, reduced: boolean): Promise<void> {
  const s = o.scale.x || 1;
  o.scale.setScalar(0.001);
  return new Promise((resolve) => {
    gsap.to(o.scale, {
      x: s,
      y: s,
      z: s,
      duration: (reduced ? 0.2 : 0.5) / speed,
      ease: 'back.out(2.2)',
      onComplete: () => {
        glint(o.parent ?? o, new THREE.Box3().setFromObject(o).getCenter(new THREE.Vector3()), { size: 1.8, speed });
        resolve();
      },
    });
  });
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

/** Storm Power: a brass pipe from the lightning column that drips mana into a little basin. */
function stormPipe(): THREE.Object3D {
  const g = new THREE.Group();
  const pink = toonFrom({ color: '#f26bbd', emissive: '#f26bbd', emissiveIntensity: 0.55 });
  const b = brass();
  const run = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 2.8, 12), b);
  run.rotation.z = Math.PI / 2;
  run.position.set(8.2, 0.34, -10.4);
  const rise = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 1.1, 12), b);
  rise.position.set(9.6, 0.85, -10.4);
  const elbow = new THREE.Mesh(new THREE.SphereGeometry(0.14, 12, 10), b);
  elbow.position.set(9.6, 0.34, -10.4);
  const spout = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 0.5, 10), b);
  spout.rotation.z = Math.PI / 2;
  spout.position.set(9.85, 1.36, -10.4);
  g.add(run, rise, elbow, spout);
  for (const x of [7.4, 8.4, 9.0]) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.04, 8, 16), pink);
    ring.rotation.y = Math.PI / 2;
    ring.position.set(x, 0.34, -10.4);
    g.add(ring);
  }
  const basin = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.26, 0.24, 16), b);
  basin.position.set(10.1, ROOM_FLOOR_Y + 0.12, -10.4);
  const pool = new THREE.Mesh(new THREE.CylinderGeometry(0.29, 0.29, 0.02, 16), pink);
  pool.position.set(10.1, ROOM_FLOOR_Y + 0.23, -10.4);
  g.add(basin, pool);
  // The drip: a drop of mana that falls from the spout into the basin, over and over.
  const drop = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), pink);
  drop.scale.y = 1.4;
  drop.position.set(10.1, 1.3, -10.4);
  g.add(drop);
  gsap.fromTo(drop.position, { y: 1.3 }, { y: ROOM_FLOOR_Y + 0.26, duration: 0.55, ease: 'power2.in', repeat: -1, repeatDelay: 0.9, data: AMBIENT });
  return g;
}

/** Open Shelves: a sandwich board by the Arcanium's door. */
function openShelvesSign(): THREE.Object3D {
  const g = new THREE.Group();
  const tex = canvasTex(512, 360, (x) => {
    x.fillStyle = '#233a2e';
    x.fillRect(0, 0, 512, 360);
    x.strokeStyle = '#c9a14e';
    x.lineWidth = 10;
    x.strokeRect(10, 10, 492, 340);
    x.fillStyle = '#f4ead0';
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.font = '700 64px "Cinzel", Georgia, serif';
    x.fillText('OPEN', 256, 88);
    x.fillText('SHELVES', 256, 162);
    x.font = '500 40px "Alegreya Sans", system-ui, sans-serif';
    x.fillStyle = '#e8c774';
    x.fillText('Refresh the display free', 256, 240);
    drawIcon(x, 'book', 256, 302, 56, '#e8c774');
  });
  const wood = toonFrom({ color: '#5a3a24' });
  const face = new THREE.Mesh(new THREE.PlaneGeometry(1.5, 1.05), new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }));
  const back = box(1.6, 1.15, 0.05, wood);
  const leanFront = new THREE.Group();
  face.position.set(0, 0, 0.03);
  leanFront.add(back, face);
  leanFront.position.set(0, 0.62, 0.22);
  leanFront.rotation.x = -0.34;
  const leanBack = box(1.6, 1.15, 0.05, wood, 0, 0.62, -0.22);
  leanBack.rotation.x = 0.34;
  g.add(leanFront, leanBack);
  g.position.set(-14.3, ROOM_FLOOR_Y, 8.6);
  g.rotation.y = 0.35;
  return g;
}

/** Ceremonial Stipend: a tip jar of mana crystals on a pedestal by the Office's desk. */
function tipJar(): THREE.Object3D {
  const g = new THREE.Group();
  const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.32, 0.95, 14), toonFrom({ color: '#3a2216' }));
  ped.position.y = 0.475;
  const top = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.32, 0.06, 14), brass());
  top.position.y = 0.98;
  const glass = new THREE.MeshStandardMaterial({ color: '#dff4ff', transparent: true, opacity: 0.32, roughness: 0.05, depthWrite: false });
  const jar = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.5, 18, 1, true), glass);
  jar.position.y = 1.27;
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.24, 0.025, 8, 20), brass());
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 1.52;
  g.add(ped, top, jar, rim);
  const pink = toonFrom({ color: '#f26bbd', emissive: '#f26bbd', emissiveIntensity: 0.35 });
  for (let i = 0; i < 7; i++) {
    const c = new THREE.Mesh(new THREE.OctahedronGeometry(0.08, 0), pink);
    c.scale.y = 1.5;
    c.position.set(Math.cos(i * 2.1) * 0.12, 1.08 + (i % 3) * 0.07, Math.sin(i * 2.1) * 0.12);
    c.rotation.set(i, i * 0.7, 0);
    g.add(c);
  }
  const tag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.34, 0.16),
    new THREE.MeshBasicMaterial({
      map: canvasTex(256, 120, (x) => {
        x.fillStyle = '#f2e6c8';
        x.fillRect(0, 0, 256, 120);
        x.fillStyle = '#5a1420';
        x.font = '700 64px "Cinzel", Georgia, serif';
        x.textAlign = 'center';
        x.textBaseline = 'middle';
        x.fillText('TIPS', 128, 64);
      }),
      toneMapped: false,
    }),
  );
  tag.position.set(0, 1.25, 0.245);
  g.add(tag);
  g.position.set(22.7, ROOM_FLOOR_Y, 16.6);
  return g;
}

/** Night Market: a striped stall by the fountain, lanterns lit, a few scrolls for sale. */
function marketStall(): THREE.Object3D {
  const g = new THREE.Group();
  const wood = toonFrom({ color: '#6a4028' });
  const [w, d] = [2.0, 1.1];
  for (const [x, z] of [
    [-w / 2, -d / 2],
    [w / 2, -d / 2],
    [w / 2, d / 2],
    [-w / 2, d / 2],
  ] as const) {
    g.add(box(0.08, 2.1, 0.08, wood, x, 1.05, z));
  }
  g.add(box(w, 0.8, 0.5, wood, 0, 0.4, d / 2 - 0.25));
  const stripes = canvasTex(256, 64, (x) => {
    for (let i = 0; i < 8; i++) {
      x.fillStyle = i % 2 ? '#f5e6c8' : '#c94f8e';
      x.fillRect(i * 32, 0, 32, 64);
    }
  });
  stripes.wrapS = THREE.RepeatWrapping;
  const awning = new THREE.Mesh(new THREE.PlaneGeometry(w + 0.4, d + 0.5), toonFrom({ map: stripes, side: THREE.DoubleSide }));
  awning.rotation.x = -Math.PI / 2 + 0.35;
  awning.position.set(0, 2.2, 0.05);
  g.add(awning);
  // Valance: little scallops along the front edge.
  for (let i = 0; i < 6; i++) {
    const sc = new THREE.Mesh(new THREE.CircleGeometry(0.17, 10, 0, Math.PI), toonFrom({ color: i % 2 ? '#f5e6c8' : '#c94f8e', side: THREE.DoubleSide }));
    sc.rotation.z = Math.PI;
    sc.position.set(-w / 2 - 0.05 + i * 0.42, 2.0, d / 2 + 0.24);
    g.add(sc);
  }
  const glow = toonFrom({ color: '#ffd28a', emissive: '#ffb45a', emissiveIntensity: 0.9 });
  for (const x of [-w / 2 + 0.25, w / 2 - 0.25]) {
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.1, 10, 8), glow);
    lamp.position.set(x, 1.72, d / 2 + 0.05);
    const cord = box(0.015, 0.3, 0.015, iron(), x, 1.92, d / 2 + 0.05);
    g.add(lamp, cord);
  }
  const schools = ['#c8443a', '#e0b43c', '#3f7fe0', '#3fa86a'];
  schools.forEach((col, i) => {
    const scroll = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.34, 10), toonFrom({ color: col }));
    scroll.rotation.z = Math.PI / 2;
    scroll.position.set(-0.6 + i * 0.4, 0.86, d / 2 - 0.25);
    g.add(scroll);
  });
  g.position.set(10.3, ROOM_FLOOR_Y, 5.1);
  g.rotation.y = -0.4;
  return g;
}

/** Memos Fly Free: three paper aeroplanes circling over the Planning Office. */
function memoPlanes(reduced: boolean): THREE.Object3D {
  const g = new THREE.Group();
  const paper = new THREE.MeshToonMaterial({ color: '#f6f0e0', side: THREE.DoubleSide });
  const shape = new THREE.Shape();
  shape.moveTo(0, 0.45);
  shape.lineTo(0.22, -0.3);
  shape.lineTo(0, -0.18);
  shape.lineTo(-0.22, -0.3);
  shape.closePath();
  const geo = new THREE.ShapeGeometry(shape);
  for (let i = 0; i < 3; i++) {
    const plane = new THREE.Group();
    const wingL = new THREE.Mesh(geo, paper);
    wingL.rotation.x = -Math.PI / 2;
    wingL.rotation.y = 0.35;
    const wingR = new THREE.Mesh(geo, paper);
    wingR.rotation.x = -Math.PI / 2;
    wingR.rotation.y = -0.35;
    plane.add(wingL, wingR);
    const a = (i / 3) * Math.PI * 2;
    plane.position.set(Math.cos(a) * 2.6, (i - 1) * 0.35, Math.sin(a) * 2.6);
    // Nose along the circle's tangent.
    plane.rotation.y = -a;
    plane.scale.setScalar(1.3);
    if (!reduced) gsap.to(plane.position, { y: plane.position.y + 0.25, duration: 1.1 + i * 0.3, yoyo: true, repeat: -1, ease: 'sine.inOut', data: AMBIENT });
    g.add(plane);
  }
  g.position.set(1.25, 2.6, 11.3);
  if (!reduced) gsap.to(g.rotation, { y: -Math.PI * 2, duration: 16, repeat: -1, ease: 'none', data: AMBIENT });
  return g;
}

/** A toll barrier: a striped arm across the door and a little booth. */
function tollGate(location: string): THREE.Object3D | null {
  const spot = TOLL_SPOTS[location];
  if (!spot) return null;
  const g = new THREE.Group();
  const stripes = canvasTex(256, 32, (x) => {
    for (let i = 0; i < 8; i++) {
      x.fillStyle = i % 2 ? '#f3ead8' : '#b8342e';
      x.fillRect(i * 32, 0, 32, 32);
    }
  });
  const post = box(0.2, 1.1, 0.2, iron(), 0, 0.55, -1.45);
  const arm = new THREE.Group();
  arm.name = 'arm';
  const bar = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 2.8), toonFrom({ map: stripes }));
  bar.position.z = 1.4;
  bar.castShadow = true;
  arm.add(bar);
  arm.position.set(0, 1.0, -1.45);
  const booth = new THREE.Group();
  booth.add(box(0.8, 1.3, 0.8, toonFrom({ color: '#6a2c32' }), 0, 0.65, 0));
  booth.add(box(1.0, 0.1, 1.0, toonFrom({ color: '#3a2216' }), 0, 1.36, 0));
  const win = box(0.5, 0.35, 0.02, toonFrom({ color: '#f3e3b0', emissive: '#f3c070', emissiveIntensity: 0.25 }), 0, 0.9, 0.41);
  booth.add(win);
  booth.position.set(0.1, 0, -2.2);
  g.add(post, arm, booth);
  g.position.set(spot.x, 0.012, spot.z);
  return g;
}

/** Labour Accord: the Interns, one per player, queue at the main entrance until the shift starts. */
function interns(view: PlayerView, lib: PropLibrary | null): THREE.Object3D {
  const g = new THREE.Group();
  view.seats.forEach((_, seat) => {
    const o = lib?.has('meeple_intern') ? lib.make('meeple_intern', { accent: SEAL_COLORS[seat]!, trim: SEAL_TRIMS[seat]! }) : box(0.4, 0.9, 0.4, toonFrom({ color: SEAL_COLORS[seat]! }), 0, 0.45, 0);
    o.scale.setScalar(1.05);
    o.position.set((seat - (view.seats.length - 1) / 2) * 0.95, 0, 0.35 * (seat % 2));
    o.rotation.y = (seat % 2 ? -1 : 1) * 0.35; // waiting, half-turned to the table
    g.add(o);
  });
  g.position.set(0, 0.012, 21.9);
  return g;
}
